import type { FC, ReactNode } from 'react'
import type { StoreSettings } from '@/types'

import { createContext, useContext, useEffect, useState } from 'react'
import { DEFAULT_SETTINGS } from '@/constants/site'

const SettingsContext = createContext<StoreSettings>(DEFAULT_SETTINGS)

interface SettingsProviderProps {
    // Pages pass the store settings through their data loaders. Pages that cannot (404, 500)
    // fall back to the last known settings, then fetch them after mount.
    settings?: StoreSettings | null
    children: ReactNode
}

export const SettingsProvider: FC<SettingsProviderProps> = ({ settings, children }): ReactNode => {
    const [known, setKnown] = useState<StoreSettings>(settings ?? DEFAULT_SETTINGS)

    useEffect(() => {
        if (settings) {
            setKnown(settings)

            return
        }

        let cancelled = false

        fetch('/api/settings')
            .then(response => (response.ok ? response.json() : null))
            .then((data: { settings?: StoreSettings } | null) => {
                if (!cancelled && data?.settings) setKnown(data.settings)
            })
            .catch(() => undefined)

        return () => {
            cancelled = true
        }
    }, [settings])

    return (
        <SettingsContext.Provider value={settings ?? known}>
            {children}
        </SettingsContext.Provider>
    )
}

export const useSettings = (): StoreSettings => useContext(SettingsContext)
